import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp, 
  MagnifyingGlass,
  Target, 
  Warning,
  CheckCircle,
  XCircle,
  ArrowRight,
  TrendUp,
  ChartBar,
  ListChecks,
  Notebook,
  Brain,
  Repeat,
  Scales,
  Eye,
  Crosshair,
  Trophy,
  ArrowsClockwise
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const HowToAnalyzeYourTrades = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'How to Analyze Your Trades | Improve Trading Performance',
      description: 'Learn how to analyze your trades step by step, track the right metrics, and improve your trading performance over time.',
      path: '/how-to-analyze-your-trades',
      lang: 'en',
      alternates: getArticleAlternates('/how-to-analyze-your-trades'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <header className="max-w-4xl mx-auto px-6 pt-16 pb-12">
        <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
          <MagnifyingGlass size={16} weight="fill" />
          <span>Trading Education</span>
        </div>
        
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
          How to Analyze Your Trades
          <span className="block text-white/60 text-2xl md:text-3xl mt-2">
            Like a Professional Trader
          </span>
        </h1>
        
        <p className="text-lg text-white/70 leading-relaxed max-w-3xl">
          Most traders take hundreds of trades but never actually improve. They repeat the same 
          mistakes, follow the same bad habits, and wonder why their results stay flat. The 
          difference between struggling and succeeding? Learning <strong className="text-white">how to analyze your trades</strong> properly. 
          If you're still using spreadsheets, consider switching to an <Link to="/automated-trading-journal" className="text-[#00E676] hover:underline">automated trading journal</Link> first.
        </p>
      </header>

      <main className="max-w-4xl mx-auto px-6 pb-20">
        
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/20 rounded-lg p-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-3">
              <Brain size={24} weight="duotone" className="text-[#00E676]" />
              Why Trade Analysis Matters
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Here's the uncomfortable truth: taking more trades won't make you better. 
              Improvement comes from <strong className="text-white">analyzing trades</strong> you've 
              already taken—understanding what worked, what didn't, and why.
            </p>
            <p className="text-white/70 leading-relaxed">
              Professional traders spend as much time reviewing as they do executing. They know 
              that <strong className="text-white">trading performance analysis</strong> is what 
              separates consistent profitability from random outcomes.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <ChartBar size={28} weight="duotone" className="text-[#00E676]" />
            What Data Matters Most
          </h2>
          
          <p className="text-white/70 mb-8 leading-relaxed">
            When you <strong className="text-white">analyze trades</strong>, focus on metrics 
            that actually predict future performance—not just your P&L.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                metric: "Win Rate",
                description: "What percentage of your trades are profitable? Aim for consistency over perfection.",
                benchmark: "Target: 50-65% depending on strategy",
                icon: Target
              },
              {
                metric: "Risk/Reward Ratio",
                description: "How much do you gain on winners vs. lose on losers? This matters more than win rate.",
                benchmark: "Target: 1.5:1 minimum average",
                icon: Scales
              },
              {
                metric: "Profit Factor",
                description: "Gross profits divided by gross losses. The single best measure of edge.",
                benchmark: "Target: Above 1.5 for consistent profitability",
                icon: TrendUp
              },
              {
                metric: "Consistency",
                description: "Are your results repeatable week over week? Streaks matter less than stability.",
                benchmark: "Target: Positive expectancy over 20+ trades",
                icon: Repeat
              },
              {
                metric: "Execution Quality",
                description: "Did you follow your plan? Entry timing, position sizing, exit discipline.",
                benchmark: "Target: 80%+ plan adherence",
                icon: Crosshair
              },
              {
                metric: "Setup Performance",
                description: "Which patterns actually make money? Track by strategy type. Learn how to find a profitable trading setup.",
                benchmark: "Target: Identify top 2-3 setups",
                icon: ChartLineUp
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-6 hover:border-[#00E676]/30 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <item.icon size={20} weight="duotone" className="text-[#00E676]" />
                  <h3 className="font-semibold">{item.metric}</h3>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-2">{item.description}</p>
                <p className="text-[#00E676]/80 text-xs font-medium">{item.benchmark}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <ListChecks size={28} weight="duotone" className="text-purple-400" />
            How to Review Trades Step by Step
          </h2>
          
          <p className="text-white/70 mb-8 leading-relaxed">
            A structured approach to <strong className="text-white">trading performance analysis</strong> ensures 
            you don't miss critical insights. Follow this process:
          </p>
          
          <div className="space-y-4">
            {[
              {
                step: "1",
                title: "Review Entry Quality",
                details: [
                  "Did you enter at your planned price?",
                  "Was the setup fully confirmed before entry?",
                  "Did you wait for your trigger or jump in early?"
                ]
              },
              {
                step: "2",
                title: "Check Exit Execution",
                details: [
                  "Did you exit at your target or stop?",
                  "Did emotions cause you to close early or hold too long?",
                  "Was your exit timing optimal for the setup?"
                ]
              },
              {
                step: "3",
                title: "Measure Risk Management",
                details: [
                  "Was your position size appropriate?",
                  "Did you honor your stop loss?",
                  "Was risk per trade within your rules?"
                ]
              },
              {
                step: "4",
                title: "Spot Repeated Mistakes",
                details: [
                  "Are you making the same errors week after week?",
                  "Which behaviors consistently hurt your results?",
                  "What patterns show up in your losing trades?"
                ]
              },
              {
                step: "5",
                title: "Identify Best Patterns",
                details: [
                  "Which setups have the highest win rate?",
                  "Which timeframes suit your style?",
                  "What conditions lead to your best trades?"
                ]
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-6 hover:border-purple-400/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-400 font-bold">{item.step}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-3">{item.title}</h3>
                    <ul className="space-y-2">
                      {item.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2 text-white/60 text-sm">
                          <CheckCircle size={14} weight="fill" className="text-purple-400 mt-0.5 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Warning size={28} weight="duotone" className="text-red-400" />
            Common Mistakes in Trade Analysis
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                mistake: "Only Looking at P&L",
                problem: "A winning trade can still be badly executed. A losing trade can be perfect.",
                fix: "Judge trades by process, not just outcome."
              },
              {
                mistake: "Ignoring Losing Trades",
                problem: "Losses contain the most valuable lessons. Skipping them means repeating them.",
                fix: "Review losses first. They're where improvement lives."
              },
              {
                mistake: "Reviewing Inconsistently",
                problem: "Analyzing trades only after bad days creates biased data.",
                fix: "Set a fixed review schedule—daily or weekly."
              },
              {
                mistake: "Tracking Too Little Data",
                problem: "If you only log entry and exit, you can't identify patterns.",
                fix: "Track setup type, timeframe, confidence, and notes."
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-6 hover:border-red-400/30 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <XCircle size={18} weight="fill" className="text-red-400" />
                  <h3 className="font-semibold text-red-400">{item.mistake}</h3>
                </div>
                <p className="text-white/60 text-sm mb-3">{item.problem}</p>
                <p className="text-white/80 text-sm flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  {item.fix}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <ArrowsClockwise size={28} weight="duotone" className="text-[#00E676]" />
            How Better Review Creates Consistency
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              When you consistently <strong className="text-white">analyze your trades</strong>, something 
              shifts. You stop reacting emotionally to individual outcomes and start seeing patterns. 
              You notice that your breakout trades work better in the morning. You realize your 
              reversals fail 70% of the time. You see that scaling out improves your results.
            </p>
            <p className="text-white/70 leading-relaxed">
              This data-driven clarity leads to better decisions. You cut what doesn't work. You 
              double down on what does. Over time, your edge compounds.
            </p>
            <p className="text-white/70 leading-relaxed">
              Many traders use tools like <strong className="text-white">GENI</strong> to simplify 
              trade analysis and reduce human bias. But regardless of your tools, the principle 
              remains: structured review creates sustainable <strong className="text-white">improvement in trading results</strong>.
            </p>
          </div>
          
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { label: "Review Regularly", icon: Eye },
              { label: "Track Everything", icon: Notebook },
              { label: "Improve Continuously", icon: Trophy }
            ].map((item, index) => (
              <div key={index} className="bg-[#121212] border border-white/10 rounded-lg p-4 text-center">
                <item.icon size={24} weight="duotone" className="text-[#00E676] mx-auto mb-2" />
                <p className="text-sm font-medium">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-2">
              <strong>Related Reading:</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/automated-trading-journal" className="text-[#00E676] hover:underline text-sm">
                What Is an Automated Trading Journal? →
              </Link>
              <Link to="/crypto-trading-journal" className="text-[#00E676] hover:underline text-sm">
                Best Trading Journal for Crypto Traders →
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-16">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/how-to-find-a-trading-setup" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Find a Trading Setup</h3>
              <p className="text-white/50 text-sm">Discover what makes a profitable trading setup.</p>
            </Link>
            <Link to="/why-traders-fail-to-improve" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Why Traders Fail to Improve</h3>
              <p className="text-white/50 text-sm">Common mistakes that keep traders stuck in place.</p>
            </Link>
            <Link to="/manual-vs-automated-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Manual vs Automated Journals</h3>
              <p className="text-white/50 text-sm">Which journaling method is right for you?</p>
            </Link>
            <Link to="/crypto-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Best Crypto Trading Journal</h3>
              <p className="text-white/50 text-sm">Specific strategies for tracking crypto trades.</p>
            </Link>
          </div>
        </section>

        <section className="mb-8">
          <div className="bg-gradient-to-br from-purple-500/10 via-[#121212] to-[#00E676]/10 border border-purple-500/30 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <ChartLineUp size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Start Analyzing Your Trades Today
            </h2>
            
            <p className="text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
              Start reviewing your trades with more clarity and improve your trading performance. 
              The best time to begin was yesterday. The second best time is now.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Get Started Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "How to Analyze Your Trades Like a Professional Trader",
              "description": "Learn how to analyze your trades step by step, track the right metrics, and improve your trading performance over time.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0]
            })
          }}
        />
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowToAnalyzeYourTrades;
